import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

// Import components
import SummaryCards from './components/SummaryCards';
import SearchFilters from './components/SearchFilters';
import InvoiceTable from './components/InvoiceTable';
import ChallanTable from './components/ChallanTable';
import DocumentPreviewModal from './components/DocumentPreviewModal';
import CreateInvoiceModal from './components/CreateInvoiceModal';
import CreateChallanModal from './components/CreateChallanModal';
import ProcessReturnModal from './components/ProcessReturnModal';

const InvoiceChallanManagement = () => {
  const [activeTab, setActiveTab] = useState('invoices');
  const [filters, setFilters] = useState({});
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  // Modal states
  const [previewModal, setPreviewModal] = useState({ isOpen: false, document: null, type: null });
  const [createInvoiceModal, setCreateInvoiceModal] = useState(false);
  const [createChallanModal, setCreateChallanModal] = useState(false);
  const [processReturnModal, setProcessReturnModal] = useState(false);

  // Prevent body scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen = previewModal.isOpen || createInvoiceModal || createChallanModal || processReturnModal;
    
    if (isAnyModalOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Apply styles to prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      // Store scroll position for restoration
      document.body.setAttribute('data-scroll-y', scrollY.toString());
    } else {
      // Restore scroll position and remove fixed positioning
      const scrollY = document.body.getAttribute('data-scroll-y');
      
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 10));
        document.body.removeAttribute('data-scroll-y');
      }
    }
    
    // Cleanup function
    return () => {
      // Reset styles if component unmounts with modal open
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.body.removeAttribute('data-scroll-y');
    };
  }, [previewModal.isOpen, createInvoiceModal, createChallanModal, processReturnModal]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // In a real app, this would trigger data refetch
    console.log('Filters updated:', newFilters);
  };

  const handlePreview = (document, type = null) => {
    const documentType = type || (activeTab === 'invoices' ? 'invoice' : 'challan');
    setPreviewModal({ isOpen: true, document, type: documentType });
  };

  const handleEdit = (document) => {
    console.log('Edit document:', document);
    // In a real app, this would open edit modal
    alert(`Edit ${activeTab.slice(0, -1)} ${document.id}`);
  };

  const handleDelete = (document) => {
    if (window.confirm(`Are you sure you want to delete ${document.id}?`)) {
      console.log('Delete document:', document);
      // In a real app, this would call delete API
      alert(`${document.id} deleted successfully`);
    }
  };

  const handleCreateInvoice = (invoiceData) => {
    console.log('Create invoice:', invoiceData);
    // In a real app, this would call create API
    alert('Invoice created successfully');
    setCreateInvoiceModal(false);
  };

  const handleCreateChallan = (challanData) => {
    console.log('Create challan:', challanData);
    // In a real app, this would call create API
    alert('Challan created successfully');
    setCreateChallanModal(false);
  };

  const handleProcessReturn = (returnData) => {
    console.log('Process return:', returnData);
    // In a real app, this would call return processing API
    alert(`Return processed: ${returnData.totalReturnedPieces} pieces returned`);
    setProcessReturnModal(false);
  };

  const handleBulkInvoiceGeneration = () => {
    alert('Bulk invoice generation feature coming soon');
  };

  const handlePaymentRecording = () => {
    alert('Payment recording feature coming soon');
  };

  // Close modal handlers that ensure proper cleanup
  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false, document: null, type: null });
  };

  const closeCreateInvoiceModal = () => {
    setCreateInvoiceModal(false);
  };

  const closeCreateChallanModal = () => {
    setCreateChallanModal(false);
  };

  const closeProcessReturnModal = () => {
    setProcessReturnModal(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar onCollapseChange={setIsSidebarCollapsed} />

      <main
        className={`
          transition-all duration-300
          pt-16
          ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
          min-h-[calc(100vh-4rem)]
          px-4 sm:px-6
        `}
      >
        <div className="flex-1 flex flex-col mb-8">
          <Breadcrumb />

          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Invoice & Challan Management
              </h1>
              <p className="text-muted-foreground">
                Manage billing documentation, delivery tracking, and return processing
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkInvoiceGeneration}
                iconName="FileStack"
                iconPosition="left"
              >
                Bulk Generate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePaymentRecording}
                iconName="CreditCard"
                iconPosition="left"
              >
                Record Payment
              </Button>
              <Button
                variant="warning"
                size="sm"
                onClick={() => setProcessReturnModal(true)}
                iconName="RotateCcw"
                iconPosition="left"
              >
                Process Return
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCreateChallanModal(true)}
                iconName="Truck"
                iconPosition="left"
              >
                Create Challan
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => setCreateInvoiceModal(true)}
                iconName="Plus"
                iconPosition="left"
              >
                Generate Invoice
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <SummaryCards />

          {/* Search and Filters */}
          <SearchFilters onFilterChange={handleFilterChange} activeTab={activeTab} />

          {/* Tabs */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6">
                <button
                  onClick={() => setActiveTab('invoices')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'invoices' 
                      ? 'border-primary text-primary' 
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={16} />
                    <span>Invoices</span>
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                      5
                    </span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('challans')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'challans' 
                      ? 'border-primary text-primary' 
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Icon name="Truck" size={16} />
                    <span>Delivery Challans</span>
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">
                      5
                    </span>
                  </div>
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'invoices' ? (
                <InvoiceTable
                  onPreview={(doc) => handlePreview(doc, 'invoice')}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ) : (
                <ChallanTable
                  onPreview={(doc) => handlePreview(doc, 'challan')}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <DocumentPreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreviewModal}
        document={previewModal.document}
        type={previewModal.type}
      />

      <CreateInvoiceModal
        isOpen={createInvoiceModal}
        onClose={closeCreateInvoiceModal}
        onSave={handleCreateInvoice}
      />

      <CreateChallanModal
        isOpen={createChallanModal}
        onClose={closeCreateChallanModal}
        onSave={handleCreateChallan}
      />

      <ProcessReturnModal
        isOpen={processReturnModal}
        onClose={closeProcessReturnModal}
        onSave={handleProcessReturn}
      />
    </div>
  );
};

export default InvoiceChallanManagement;